import {
  Autocomplete,
  Box,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { type RestoreAccount, Provider } from '@repo/api-gw-sdk';
import { type Dispatch, type SetStateAction } from 'react';

import { TransactionalTextField } from '@/components/shared/transactionalTextField';

import { type NormalizedBucket } from './useBuckets';

import { Icon } from '../shared/icon';

export interface RestoreGenericStorageProps {
  region?: string;
  prefix?: string;
  S3: { Bucket?: string };
  GCS: { Bucket?: string };
  StorageAccount: {
    AccountName?: string;
    ResourceGroup?: string;
    Container?: string;
  };
}

interface BucketSelectionProps<T> extends RestoreGenericStorageProps {
  restoreAccount?: RestoreAccount;
  buckets: NormalizedBucket[];
  missingListPermissions?: boolean;
  isLoading?: boolean;
  setBucketSelectionState: Dispatch<
    SetStateAction<
      T & {
        restoreAccount?: RestoreAccount;
      }
    >
  >;
}

export const BucketSelection = <T extends RestoreGenericStorageProps>(
  props: BucketSelectionProps<T>
) => {
  const { prefix, setBucketSelectionState, buckets, isLoading } = props;
  const stateKey = {
    [Provider.Aws]: 'S3' as const,
    [Provider.Gcp]: 'GCS' as const,
    [Provider.Azure]: null,
    [Provider.MongoAtlas]: null,
    [Provider.ProviderUnspecified]: null,
  }[
    props.restoreAccount?.restoreAccountAttributes.cloudProvider ??
      Provider.ProviderUnspecified
  ];

  const setRestoreBucketName = (value: string) => {
    if (!stateKey) {
      return;
    }
    setBucketSelectionState((state) => ({
      ...state,
      [stateKey]: { ...state[stateKey], Bucket: value },
      region: state.region || props.region,
    }));
  };

  const setRestorePrefix = (value: string) => {
    setBucketSelectionState((state) => ({
      ...state,
      prefix: value,
    }));
  };

  const currentBucketName = stateKey ? props[stateKey].Bucket : '';

  return (
    <Box className='mx-[40px] my-[24px]'>
      <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
        <FormControl size='small' className='flex-1'>
          <Stack
            direction='row'
            gap='8px'
            alignItems='center'
            justifyContent='space-between'
            className='group/missingListPermissions'
          >
            <FormLabel>Bucket</FormLabel>
            {(props.missingListPermissions || !buckets.length) && (
              <Tooltip
                title={
                  props.missingListPermissions
                    ? 'Paste the name of the bucket you want to restore to. To allow Eon to list your buckets, reinstall the restore account permissions.'
                    : 'No buckets found.'
                }
              >
                <Icon
                  className='opacity-0 group-hover/missingListPermissions:opacity-100 transition-opacity'
                  iconClass='material-symbols-help-outline-rounded w-[16px] h-[16px]'
                />
              </Tooltip>
            )}
          </Stack>
          {props.missingListPermissions ? (
            <TransactionalTextField
              initValue={currentBucketName || ''}
              onChange={setRestoreBucketName}
              disabled={isLoading}
              placeholder='Example: my-bucket-name'
            />
          ) : (
            <Autocomplete
              data-testid='bucketSelect'
              disabled={isLoading || !buckets.length}
              filterOptions={(options, state) => {
                const input = state.inputValue.trim().toLowerCase();
                if (!input) return options;
                return options.filter((option) => {
                  const bucketName = option.bucketName.toLowerCase();
                  const region = option.regionName?.toLowerCase() ?? '';
                  return bucketName.includes(input) || region.includes(input);
                });
              }}
              clearOnBlur
              size='small'
              value={
                (stateKey &&
                  buckets.find(
                    (x) => x.bucketName === props[stateKey].Bucket
                  )) ||
                null
              }
              getOptionDisabled={(option) => option.locationType === 'zone'}
              renderOption={(props, option) => (
                <li {...props} key={option.bucketName}>
                  <BucketRegionItem
                    bucketName={option.bucketName}
                    regionName={option.regionName}
                    locationType={option.locationType}
                  />
                </li>
              )}
              onChange={(event, newValue) =>
                setRestoreBucketName(newValue?.bucketName || '')
              }
              getOptionLabel={(option) => option.bucketName}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    '& .MuiInputBase-input': {
                      height: '40px',
                    },
                  }}
                  placeholder='Select'
                  label=''
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
              options={buckets}
            />
          )}
        </FormControl>
        <BucketPrefix prefix={prefix} onChange={setRestorePrefix} />
      </Stack>
    </Box>
  );
};

const BucketRegionItem = (props: {
  bucketName: string;
  regionName: string;
  locationType?: string;
}) => {
  const isZonal = props.locationType === 'zone';

  return (
    <Tooltip
      title={
        isZonal ? 'Zonal buckets are not supported as restore targets' : ''
      }
    >
      <Stack direction='column' className='truncate'>
        <Typography color={isZonal ? 'text.disabled' : undefined}>
          {props.bucketName}
        </Typography>
        <Typography
          variant='body2'
          color={isZonal ? 'text.disabled' : undefined}
        >
          {props.regionName}
          {isZonal && ' (zonal)'}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const BucketPrefix = (props: {
  prefix?: string;
  onChange: (value: string) => void;
}) => {
  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Prefix (optional)</FormLabel>
      <TransactionalTextField
        initValue={props.prefix || ''}
        onChange={props.onChange}
        placeholder='Example: project/wordFiles/'
      />
    </FormControl>
  );
};

export const getStorageName = (
  bucket?: RestoreGenericStorageProps,
  cloudProvider?: Provider
) => {
  if (!bucket) {
    return;
  }

  switch (cloudProvider) {
    case Provider.Aws:
      return bucket.S3?.Bucket;
    case Provider.Gcp:
      return bucket.GCS?.Bucket;
    case Provider.Azure:
      return bucket.StorageAccount?.AccountName;
  }
};
